/**
 * Challenge Screenshot Card
 *
 * Shows the latest LinkedIn login challenge screenshot (2FA prompt, captcha,
 * or third-party check) fetched from an authenticated API route. For captcha
 * and third-party challenges the image is clickable: the click position is
 * translated to screenshot pixel coordinates and sent back into the chat,
 * where the assistant submits it via solveCaptchaClick /
 * submitThirdPartyChallenge.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Paper, Typography, Button, CircularProgress, Chip } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import RefreshIcon from '@mui/icons-material/Refresh';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { API_V1 } from '../../../API/APIs/APIv1';
import { ChallengeScreenshotCardData } from '../../../types/ai-chat.types';
import { useAIChat } from '../../../hooks/use-ai-chat';

interface ChallengeScreenshotCardProps {
  data: ChallengeScreenshotCardData;
}

const KIND_LABELS: Record<string, { key: string; fallback: string }> = {
  '2fa': { key: 'twoFaChallenge', fallback: '2FA Verification' },
  captcha: { key: 'captchaChallenge', fallback: 'Captcha Challenge' },
  third_party: { key: 'securityChallenge', fallback: 'Security Challenge' },
};

const ChallengeScreenshotCard: React.FC<ChallengeScreenshotCardProps> = ({ data }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { send, isSending } = useAIChat();
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastClick, setLastClick] = useState<{ x: number; y: number } | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  // The PNG route requires the Bearer token, so a plain <img src> won't work.
  // Fetch as blob through the authed axios instance and render an object URL.
  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;

    setIsLoading(true);
    setLoadError(null);

    API_V1.get(data.imageUrl, { responseType: 'blob' })
      .then((response) => {
        if (cancelled) return;
        const url = URL.createObjectURL(response.data as Blob);
        revoked = url;
        setObjectUrl(url);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(
          t('challengeScreenshotUnavailable', 'Screenshot not available yet. Try refreshing in a moment.')
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [data.imageUrl, reloadNonce, t]);

  const submitClickAt = useCallback(
    (x: number, y: number) => {
      setLastClick({ x, y });
      const kindLabel = data.kind === 'captcha' ? 'Captcha' : 'Challenge';
      send(
        `${kindLabel} click for account ${data.accountId}: x=${x}, y=${y}. Please submit it and check the status.`
      );
    },
    [data.accountId, data.kind, send]
  );

  const handleImageClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!data.clickable || !imgRef.current || isSending) return;

      // Translate the click from rendered size to natural screenshot pixels
      const img = imgRef.current;
      const rect = img.getBoundingClientRect();
      const scaleX = img.naturalWidth / rect.width;
      const scaleY = img.naturalHeight / rect.height;
      const x = Math.round((event.clientX - rect.left) * scaleX);
      const y = Math.round((event.clientY - rect.top) * scaleY);
      submitClickAt(x, y);
    },
    [data.clickable, isSending, submitClickAt]
  );

  // Keyboard fallback: Enter/Space submits a click at the image center —
  // coordinate-precise selection needs a pointer, but this keeps the control
  // reachable for keyboard users.
  const handleImageKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (!data.clickable || !imgRef.current || isSending) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      const img = imgRef.current;
      submitClickAt(Math.round(img.naturalWidth / 2), Math.round(img.naturalHeight / 2));
    },
    [data.clickable, isSending, submitClickAt]
  );

  const label = KIND_LABELS[data.kind] || KIND_LABELS.captcha;

  return (
    <Paper className={classes.card}>
      <Box className={classes.cardHeader}>
        <SecurityIcon className={classes.cardIcon} />
        <Typography variant="subtitle2">{t(label.key, label.fallback)}</Typography>
        {data.capturedAt && (
          <Chip
            size="small"
            className={classes.timeChip}
            label={new Date(data.capturedAt).toLocaleTimeString()}
          />
        )}
      </Box>

      {data.clickable && (
        <Typography variant="caption" color="textSecondary" className={classes.hint}>
          {t('challengeClickHint', 'Click directly on the image where the challenge asks you to click.')}
        </Typography>
      )}

      <Box className={classes.imageWrap}>
        {isLoading && <CircularProgress size={28} className={classes.spinner} />}
        {!isLoading && loadError && (
          <Typography variant="body2" color="textSecondary" className={classes.errorText}>
            {loadError}
          </Typography>
        )}
        {!isLoading && !loadError && objectUrl && (
          data.clickable ? (
            <Box
              role="button"
              tabIndex={0}
              aria-label={t('challengeClickHint', 'Click directly on the image where the challenge asks you to click.')}
              onClick={handleImageClick}
              onKeyDown={handleImageKeyDown}
              className={classes.clickTarget}
            >
              <img
                ref={imgRef}
                src={objectUrl}
                alt={t(label.key, label.fallback)}
                className={classes.clickableImage}
              />
            </Box>
          ) : (
            <img
              ref={imgRef}
              src={objectUrl}
              alt={t(label.key, label.fallback)}
              className={classes.image}
            />
          )
        )}
      </Box>

      <Box className={classes.footer}>
        {lastClick && (
          <Typography variant="caption" color="textSecondary">
            {t('challengeClickSent', 'Click sent')}: x={lastClick.x}, y={lastClick.y}
          </Typography>
        )}
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => setReloadNonce((n) => n + 1)}
          disabled={isLoading}
          className={classes.refreshButton}
        >
          {t('refreshScreenshot', 'Refresh')}
        </Button>
      </Box>
    </Paper>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.divider}`,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  cardIcon: {
    fontSize: '1.25rem',
    color: theme.palette.warning.main,
  },
  timeChip: {
    marginLeft: 'auto',
    height: 22,
    fontSize: '0.7rem',
  },
  hint: {
    display: 'block',
    marginBottom: theme.spacing(1),
  },
  imageWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 120,
    backgroundColor: theme.palette.grey[50],
    borderRadius: 6,
    overflow: 'hidden',
  },
  spinner: {
    margin: theme.spacing(3),
  },
  errorText: {
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  image: {
    maxWidth: '100%',
    display: 'block',
  },
  clickTarget: {
    display: 'block',
    lineHeight: 0,
    outlineOffset: 2,
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
    },
  },
  clickableImage: {
    maxWidth: '100%',
    display: 'block',
    cursor: 'crosshair',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
  },
  refreshButton: {
    marginLeft: 'auto',
  },
}));

export default ChallengeScreenshotCard;
